import React, { useState } from 'react';
import {
  Bell, Mail, Smartphone, Monitor, MessageSquare, Clock,
  ShoppingCart, Wrench, DollarSign, Server, Database, Shield,
  User, Save, Volume2, VolumeX
} from 'lucide-react';
import { NotificationSettings as NotificationSettingsType } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

const NotificationSettingsPage: React.FC = () => {
  const { t } = useLanguage();
  const [settings, setSettings] = useState<NotificationSettingsType>({
    id: 1,
    user_id: 1,
    email_notifications: true,
    push_notifications: true,
    desktop_notifications: true,
    sms_notifications: false,
    notification_categories: {
      orders: true,
      services: true,
      capex: true,
      system: true,
      backup: true,
      security: true
    },
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    digest_frequency: 'daily',
    created_at: '2024-01-01T00:00:00Z',
    updated_at: '2024-01-15T10:00:00Z'
  });

  const categoryOptions = [
    { key: 'orders', label: t('notifSettings.catOrders'), icon: ShoppingCart, description: t('notifSettings.catOrdersDesc') },
    { key: 'services', label: t('notifSettings.catServices'), icon: Wrench, description: t('notifSettings.catServicesDesc') },
    { key: 'capex', label: t('notifSettings.catCapex'), icon: DollarSign, description: t('notifSettings.catCapexDesc') },
    { key: 'system', label: t('notifSettings.catSystem'), icon: Server, description: t('notifSettings.catSystemDesc') },
    { key: 'backup', label: t('notifSettings.catBackup'), icon: Database, description: t('notifSettings.catBackupDesc') },
    { key: 'security', label: t('notifSettings.catSecurity'), icon: Shield, description: t('notifSettings.catSecurityDesc') }
  ];

  const digestOptions = [
    { value: 'never', label: t('notifSettings.digestNever') },
    { value: 'daily', label: t('notifSettings.digestDaily') },
    { value: 'weekly', label: t('notifSettings.digestWeekly') },
    { value: 'monthly', label: t('notifSettings.digestMonthly') }
  ];

  const handleSave = () => {
    // Simulate API call
    setTimeout(() => {
    }, 500);
    // Here would be the API call to save settings
  };

  const testNotification = (type: 'email' | 'push' | 'desktop' | 'sms') => {
    // Here would be the API call to send test notification
    
    // Show toast notification for desktop test
    if (type === 'desktop') {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Test Bildirimi', {
          body: 'Bu bir test bildirimidir.',
          icon: '/favicon.ico'
        });
      }
    }
  };



  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('notifSettings.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400">{t('notifSettings.subtitle')}</p>
      </div>

      <div className="max-w-4xl space-y-6">
        {/* Notification Types */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('notifSettings.typesTitle')}</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-primary mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('notifSettings.emailNotif')}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('notifSettings.emailNotifDesc')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => testNotification('email')}
                  className="px-3 py-1 text-xs bg-primary text-white rounded hover:bg-primary-dark transition-colors"
                >
                  Test
                </button>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.email_notifications}
                    onChange={(e) => setSettings(prev => ({ ...prev, email_notifications: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-gray-800 after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center">
                <Smartphone className="w-5 h-5 text-green-600 mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('notifSettings.pushNotif')}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('notifSettings.pushNotifDesc')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => testNotification('push')}
                  className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                >
                  Test
                </button>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.push_notifications}
                    onChange={(e) => setSettings(prev => ({ ...prev, push_notifications: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-gray-800 after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center">
                <Monitor className="w-5 h-5 text-purple-600 mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('notifSettings.desktopNotif')}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('notifSettings.desktopNotifDesc')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => testNotification('desktop')}
                  className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                >
                  Test
                </button>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.desktop_notifications}
                    onChange={(e) => setSettings(prev => ({ ...prev, desktop_notifications: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-gray-800 after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center">
                <MessageSquare className="w-5 h-5 text-orange-600 mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('notifSettings.smsNotif')}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{t('notifSettings.smsNotifDesc')}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => testNotification('sms')}
                  className="px-3 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors"
                >
                  Test
                </button>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.sms_notifications}
                    onChange={(e) => setSettings(prev => ({ ...prev, sms_notifications: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-gray-800 after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Categories */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('notifSettings.categoriesTitle')}</h3>
          
          <div className="space-y-4">
            {categoryOptions.map((category) => {
              const IconComponent = category.icon;
              const isEnabled = settings.notification_categories[category.key as keyof typeof settings.notification_categories];
              
              return (
                <div key={category.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex items-center">
                    <IconComponent className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-3" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{category.label}</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{category.description}</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        notification_categories: {
                          ...prev.notification_categories,
                          [category.key]: e.target.checked
                        }
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-gray-800 after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-3" />
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('notifSettings.quietHoursTitle')}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('notifSettings.quietHoursDesc')}</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.quiet_hours_enabled}
                onChange={(e) => setSettings(prev => ({ ...prev, quiet_hours_enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:bg-gray-800 after:border-gray-300 dark:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          {settings.quiet_hours_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('notifSettings.quietStart')}
                </label>
                <input
                  type="time"
                  value={settings.quiet_hours_start}
                  onChange={(e) => setSettings(prev => ({ ...prev, quiet_hours_start: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('notifSettings.quietEnd')}
                </label>
                <input
                  type="time"
                  value={settings.quiet_hours_end}
                  onChange={(e) => setSettings(prev => ({ ...prev, quiet_hours_end: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>

        {/* Digest Settings */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center mb-4">
            <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-3" />
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('notifSettings.digestTitle')}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{t('notifSettings.digestDesc')}</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('notifSettings.digestFrequency')}
            </label>
            <select
              value={settings.digest_frequency}
              onChange={(e) => setSettings(prev => ({ ...prev, digest_frequency: e.target.value as any }))}
              className="w-full md:w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {digestOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            className="flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Save className="w-4 h-4 mr-2" />
            {t('notifSettings.saveBtn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;